import { useState } from "react";
import { Facebook, Instagram, MessageCircle, Mail, MapPin, Shield, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import { useI18n } from "../lib/i18n";

export const Footer = () => {
  const { t } = useI18n();
  const [privacyOpen, setPrivacyOpen] = useState(false);

  return (
    <footer data-testid="site-footer" className="bg-[#1A1A1A] text-white pt-16 pb-10">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <div className="grid md:grid-cols-3 gap-10">
          <div>
            <div className="flex items-center gap-3">
              <span className="h-10 w-10 rounded-full bg-[#FFB800] text-[#1A1A1A] grid place-items-center font-black text-sm">R&V</span>
              <span className="font-extrabold tracking-tight text-lg" style={{ fontFamily: "'Outfit', system-ui, sans-serif" }}>R&V VILLA</span>
            </div>
            <p className="mt-4 text-white/70 text-sm leading-relaxed max-w-xs">{t.footer.tagline}</p>
          </div>

          <div className="space-y-3 text-sm">
            <a href="https://wa.me/18764104131" target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp" className="flex items-center gap-2 text-white/85 hover:text-[#FFB800] transition-colors">
              <MessageCircle className="w-4 h-4" />+1 876 410 4131
            </a>
            <a href="mailto:rvvilla2020@gmail.com" data-testid="footer-email" className="flex items-center gap-2 text-white/85 hover:text-[#FFB800] transition-colors">
              <Mail className="w-4 h-4" />rvvilla2020@gmail.com
            </a>
            <div className="flex items-center gap-2 text-white/85">
              <MapPin className="w-4 h-4" />
              <span className="text-white/70">{t.footer.address}</span>
            </div>
          </div>

          <div>
            <span className="text-xs uppercase tracking-[0.2em] text-white/50">Social</span>
            <div className="mt-4 flex items-center gap-3">
              <a href="https://www.facebook.com/profile.php?id=100038284699594" target="_blank" rel="noopener noreferrer" aria-label="Facebook" data-testid="social-facebook" className="w-11 h-11 rounded-full bg-white/5 hover:bg-[#FFB800] hover:text-[#1A1A1A] grid place-items-center transition-all duration-300 hover:-translate-y-0.5">
                <Facebook className="w-4 h-4" />
              </a>
              <a href="https://www.instagram.com/r_v_villa" target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="social-instagram" className="w-11 h-11 rounded-full bg-white/5 hover:bg-[#FFB800] hover:text-[#1A1A1A] grid place-items-center transition-all duration-300 hover:-translate-y-0.5">
                <Instagram className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-white/50">
          <span>© {new Date().getFullYear()} R&V Villa. {t.footer.rights}</span>
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => setPrivacyOpen(true)}
              data-testid="privacy-link"
              className="inline-flex items-center gap-1.5 text-white/70 hover:text-[#FFB800] transition-colors"
            >
              <Shield className="w-3.5 h-3.5" />
              {t.footer.privacy}
            </button>
            <span className="hidden md:inline">{t.footer.made}</span>
          </div>
        </div>
      </div>

      <PrivacyDialog open={privacyOpen} onClose={() => setPrivacyOpen(false)} />
    </footer>
  );
};

const PrivacyDialog = ({ open, onClose }) => {
  const { t } = useI18n();
  const p = t.privacy;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="privacy-dialog" className="max-w-2xl max-h-[85vh] overflow-y-auto p-0 bg-white border-none [&>button]:hidden">
        <div className="sticky top-0 bg-white/95 backdrop-blur-md border-b border-black/5 px-6 md:px-8 py-4 flex items-center justify-between z-10">
          <DialogTitle asChild>
            <h2 className="inline-flex items-center gap-2 font-bold text-[#1A1A1A] text-lg md:text-xl" style={{ fontFamily: "'Outfit', system-ui, sans-serif" }}>
              <Shield className="w-5 h-5 text-[#FFB800]" />
              {p.title}
            </h2>
          </DialogTitle>
          <button type="button" onClick={onClose} data-testid="privacy-close" aria-label={p.close} className="w-9 h-9 rounded-full bg-black/5 text-[#1A1A1A] hover:bg-[#FFB800] grid place-items-center transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-6 md:px-8 py-6 md:py-8">
          <p className="text-[#4A4A4A] text-sm md:text-base leading-relaxed">{p.subtitle}</p>

          <div className="mt-6 space-y-5">
            {p.sections.map((s, i) => (
              <div key={i} className="rounded-2xl bg-[#FAFAFA] border border-black/5 p-5">
                <h3 className="font-bold text-[#1A1A1A] text-sm md:text-base" style={{ fontFamily: "'Outfit', system-ui, sans-serif" }}>{s.title}</h3>
                <p className="mt-2 text-[#4A4A4A] text-sm leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>

          <div className="mt-6 text-xs md:text-sm text-[#4A4A4A]">
            <span className="inline-flex items-center gap-1.5">
              <Mail className="w-3.5 h-3.5 text-[#FFB800]" />
              {p.contact}
            </span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default Footer;
